export interface ReferralProfile {
  id: string;
  brand_id: string;
  contact_id: string;
  referral_code: string;
  created_at: string;
  updated_at: string;
}

/** Milestone reward tier for milestone-type programs */
export interface ReferralMilestone {
  threshold: number;
  name: string;
  image?: string | null;
}

/** Settings for milestone programs - stored in os_referral_programs.settings */
export interface ReferralProgramMilestoneSettings {
  milestones?: ReferralMilestone[];
}

export interface ReferralProgram {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  /** URL-safe identifier for share links, e.g. /join/{slug}/{ref}. Unique per brand. */
  slug: string;
  type: 'milestone' | 'waiting_list' | 'generic';
  description?: string | null;
  settings?: ReferralProgramMilestoneSettings & Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  /** Participant count (from os_referral_program_members). Fetched when listing programs. */
  member_count?: number;
}

export interface ReferralProgramMember {
  id: string;
  program_id: string;
  contact_id: string;
  /** Contact who referred this person into this program (set when they join via referral link). */
  referred_by_contact_id?: string | null;
  status: 'active' | 'converted' | 'cancelled';
  position?: number | null;
  metadata?: Record<string, unknown> | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  organization?: string | null;
  phone?: string | null;
  created_at: string;
  updated_at: string;
}

/** Enrollment trigger / per-email stop condition shape for referral campaigns */
export interface ReferralCampaignTriggerConditions {
  member_status?: string[];
}

/** Options for program member status in trigger/stop conditions */
export const REFERRAL_MEMBER_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'converted', label: 'Converted' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export interface ReferralCampaign {
  id: string;
  brand_id: string;
  user_id: string;
  program_id?: string | null;
  name: string;
  description?: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed';
  /** automatic = sequence runs on its own (e.g. on event, no dispatch); manual = initiated via Dispatch Center (can have dispatch). Defaults to automatic if missing. */
  flow_type?: 'automatic' | 'manual';
  /** Usage notes: e.g. Welcome/onboarding, Re-engagement, Milestone celebration */
  notes?: string | null;
  /** A/B variant label, e.g. Control, Variant A */
  variant_label?: string | null;
  /** Optional UUID to group A/B variants */
  variant_group?: string | null;
  metadata?: Record<string, unknown> | null;
  trigger_conditions?: ReferralCampaignTriggerConditions | null;
  email_count?: number;
  /** Joined from os_referral_programs for display */
  program?: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

export interface ReferralCampaignEmail {
  id: string;
  campaign_id: string;
  sequence_order: number;
  subject: string;
  content: string;
  delay_days: number;
  delay_hours: number;
  send_conditions?: Record<string, unknown> | null;
  stop_conditions?: ReferralCampaignTriggerConditions | null;
  prompt_id?: string | null;
  created_at: string;
  updated_at: string;
}

export type ReferralListType = 'static' | 'dynamic';

export type ReferralListCriteriaType =
  | 'never_invited'
  | 'invited_gt'
  | 'whales'
  | 'program_members'
  | 'converted';

export interface ReferralListCriteria {
  type: ReferralListCriteriaType;
  program_id?: string | null;
  params?: { min_invites?: number };
}

export interface ReferralList {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  description?: string | null;
  list_type?: ReferralListType;
  criteria?: ReferralListCriteria | null;
  total_contacts: number;
  active_contacts: number;
  created_at: string;
  updated_at: string;
}

export interface ReferralListMember {
  id: string;
  list_id: string;
  contact_id?: string | null;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  custom_fields?: Record<string, unknown> | null;
  status: 'active' | 'unsubscribed' | 'bounced';
  created_at: string;
  updated_at: string;
}

export interface ReferralDispatch {
  id: string;
  campaign_id: string;
  list_id: string;
  brand_id: string;
  user_id: string;
  status: 'pending' | 'sending' | 'completed' | 'failed';
  total_recipients: number;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}

export interface ReferralSend {
  id: string;
  dispatch_id: string;
  campaign_email_id: string;
  recipient_email: string;
  recipient_data?: Record<string, unknown> | null;
  status: 'scheduled' | 'sent' | 'failed' | 'replied' | 'unsubscribed';
  scheduled_at: string;
  sent_at?: string | null;
  opened_at?: string | null;
  replied_at?: string | null;
  error_message?: string | null;
  esp_message_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReferralPrompt {
  id: string;
  brand_id: string;
  prompt_type: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}

/** Contact shape compatible with os_contacts (module/activity for referral). */
export interface ReferralContact {
  id: string;
  brand_id: string;
  user_id?: string | null;
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  organization?: string | null;
  title?: string | null;
  module?: string[] | null;
  activity?: string[] | null;
  created_at: string;
  updated_at: string;
}
